/**
 * /app/ruleta — Configuración Ruleta (SCRUM-216 UI + SCRUM-228 orígenes).
 */
import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { Dices, Loader2, Plus, Trash2 } from "lucide-react";
import { CrudShellHeader } from "@/components/ds/CrudShell";
import { ClinicalPageShell } from "@/components/clinical";
import { FeatureLockedState } from "@/components/workspace/FeatureLockedState";
import type { PlanApiErrorPayload } from "@structura/core/plan-api-errors";
import { useWorkspaceTenantId } from "@/hooks/use-workspace-tenant-id";
import { apiGetJson, apiSendJson } from "@/lib/sm-api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import {
  DEFAULT_DISCOUNT_WHEEL_PRIZES,
  prizeOriginLabel,
  type DiscountWheelConfig,
  type DiscountWheelPrize,
  type DiscountWheelPrizeKind,
} from "@/lib/discount-wheel";

export const Route = createFileRoute("/app/ruleta")({
  component: RuletaPage,
  head: () => ({ meta: [{ title: "Ruleta de descuentos — Structura Market" }] }),
});

type CouponOpt = { id: string; code: string; name: string; active: boolean };
type PromoOpt = { id: string; name: string; active: boolean };

function newPrize(): DiscountWheelPrize {
  return {
    id: `p${Math.random().toString(36).slice(2, 8)}`,
    label: "Nuevo premio",
    weight: 10,
    kind: "existing_coupon",
    value: 0,
    templateCouponId: null,
    promotionId: null,
  };
}

function RuletaPage() {
  const tenantId = useWorkspaceTenantId();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [lock, setLock] = useState<PlanApiErrorPayload | null>(null);
  const [enabled, setEnabled] = useState(false);
  const [requireEmail, setRequireEmail] = useState(true);
  const [headline, setHeadline] = useState("¡Girá la ruleta!");
  const [prizes, setPrizes] = useState<DiscountWheelPrize[]>([...DEFAULT_DISCOUNT_WHEEL_PRIZES]);
  const [coupons, setCoupons] = useState<CouponOpt[]>([]);
  const [promos, setPromos] = useState<PromoOpt[]>([]);

  const load = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    setLock(null);
    try {
      const [res, couponsRes, promosRes] = await Promise.all([
        apiGetJson<{ config: DiscountWheelConfig }>("/api/discount-wheel", tenantId),
        apiGetJson<{ coupons: CouponOpt[] }>("/api/coupons", tenantId).catch(() => ({ coupons: [] })),
        apiGetJson<{ promotions: PromoOpt[] }>("/api/promotions", tenantId).catch(() => ({
          promotions: [],
        })),
      ]);
      const c = res.config;
      setEnabled(Boolean(c.enabled));
      setRequireEmail(c.requireEmail !== false);
      setHeadline(c.headline || "¡Girá la ruleta!");
      setPrizes(c.prizes?.length ? c.prizes : [...DEFAULT_DISCOUNT_WHEEL_PRIZES]);
      setCoupons(couponsRes.coupons ?? []);
      setPromos(promosRes.promotions ?? []);
    } catch (e: unknown) {
      const err = e as Error & { planLock?: PlanApiErrorPayload };
      if (err.planLock) setLock(err.planLock);
      else toast.error(err instanceof Error ? err.message : "No se pudo cargar la ruleta");
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    void load();
  }, [load]);

  const updatePrize = (id: string, patch: Partial<DiscountWheelPrize>) => {
    setPrizes((prev) => prev.map((p) => (p.id === id ? { ...p, ...patch } : p)));
  };

  const save = async () => {
    if (!tenantId) return;
    if (!prizes.length) {
      toast.error("Agregá al menos un premio");
      return;
    }
    for (const prize of prizes) {
      if (
        (prize.kind === "existing_coupon" || prize.kind === "template") &&
        !prize.templateCouponId
      ) {
        toast.error(`Elegí un cupón para “${prize.label}”`);
        return;
      }
      if (prize.kind === "existing_promotion" && !prize.promotionId) {
        toast.error(`Elegí una promoción para “${prize.label}”`);
        return;
      }
    }
    setSaving(true);
    try {
      const res = await apiSendJson<{ config: DiscountWheelConfig }>(
        "/api/discount-wheel",
        "PATCH",
        { enabled, requireEmail, headline: headline.trim() || "¡Girá la ruleta!", prizes },
        tenantId,
      );
      setEnabled(Boolean(res.config.enabled));
      setPrizes(res.config.prizes?.length ? res.config.prizes : prizes);
      toast.success(
        res.config.enabled
          ? "Ruleta activa — se muestra al entrar a la tienda pública"
          : "Configuración guardada (ruleta apagada)",
      );
    } catch (e: unknown) {
      const err = e as Error & { planLock?: PlanApiErrorPayload };
      if (err.planLock) setLock(err.planLock);
      else toast.error(err instanceof Error ? err.message : "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  if (lock) return <FeatureLockedState payload={lock} />;

  if (loading) {
    return (
      <div className="flex items-center gap-2 p-6 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Cargando ruleta…
      </div>
    );
  }

  return (
    <div>
      <CrudShellHeader
        title="Ruleta de descuentos"
        description="Premios sobre cupones y promociones existentes"
        actions={
          <Button type="button" variant="outline" size="sm" className="gap-1.5" onClick={() => setPrizes((p) => [...p, newPrize()])}>
            <Plus className="h-4 w-4" /> Premio
          </Button>
        }
      />
      <ClinicalPageShell offset="none" className="space-y-6 pt-6">
        <div className="flex flex-wrap items-center gap-4 rounded-xl border border-border bg-card p-4">
          <Dices className="h-5 w-5 text-muted-foreground" />
          <div className="flex items-center gap-2">
            <Switch checked={enabled} onCheckedChange={setEnabled} id="wheel-enabled" />
            <Label htmlFor="wheel-enabled">Mostrar ruleta en la tienda</Label>
          </div>
          <div className="flex items-center gap-2">
            <Switch checked={requireEmail} onCheckedChange={setRequireEmail} id="wheel-email" />
            <Label htmlFor="wheel-email">Pedir email</Label>
          </div>
          <div className="min-w-[14rem] flex-1 space-y-1">
            <Label className="text-xs">Título</Label>
            <Input value={headline} onChange={(e) => setHeadline(e.target.value)} />
          </div>
        </div>

        <div className="space-y-3">
          <p className="text-sm font-medium text-foreground">Premios</p>
          <p className="text-xs text-muted-foreground">
            Preferí cupón o promoción ya creados. “Cupón nuevo” genera un one-shot vía Coupon Engine (legado).
          </p>
          {prizes.map((prize) => (
            <div key={prize.id} className="grid gap-2 rounded-lg border p-3 sm:grid-cols-12">
              <div className="sm:col-span-3 space-y-1">
                <Label className="text-xs">Nombre</Label>
                <Input
                  value={prize.label}
                  onChange={(e) => updatePrize(prize.id, { label: e.target.value })}
                />
                <p className="text-[11px] text-muted-foreground">Origen: {prizeOriginLabel(prize)}</p>
              </div>
              <div className="sm:col-span-3 space-y-1">
                <Label className="text-xs">Tipo</Label>
                <select
                  className="flex h-9 w-full rounded-md border border-input bg-transparent px-2 text-sm"
                  value={prize.kind === "template" ? "existing_coupon" : prize.kind}
                  onChange={(e) => {
                    const kind = e.target.value as DiscountWheelPrizeKind;
                    updatePrize(prize.id, {
                      kind,
                      value: kind === "percent" || kind === "fixed" ? prize.value ?? 5 : 0,
                    });
                  }}
                >
                  <option value="existing_coupon">Cupón existente</option>
                  <option value="existing_promotion">Promoción existente</option>
                  <option value="percent">Cupón nuevo (%)</option>
                  <option value="fixed">Cupón nuevo ($)</option>
                  <option value="noop">Sin premio</option>
                </select>
              </div>
              {(prize.kind === "existing_coupon" || prize.kind === "template") && (
                <div className="sm:col-span-3 space-y-1">
                  <Label className="text-xs">Cupón</Label>
                  <select
                    className="flex h-9 w-full rounded-md border border-input bg-transparent px-2 text-sm"
                    value={prize.templateCouponId ?? ""}
                    onChange={(e) => updatePrize(prize.id, { templateCouponId: e.target.value || null })}
                  >
                    <option value="">Elegir…</option>
                    {coupons.map((c) => (
                      <option key={c.id} value={c.id} disabled={!c.active}>
                        {c.code} — {c.name}{c.active ? "" : " (inactivo)"}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              {prize.kind === "existing_promotion" && (
                <div className="sm:col-span-3 space-y-1">
                  <Label className="text-xs">Promoción</Label>
                  <select
                    className="flex h-9 w-full rounded-md border border-input bg-transparent px-2 text-sm"
                    value={prize.promotionId ?? ""}
                    onChange={(e) => updatePrize(prize.id, { promotionId: e.target.value || null })}
                  >
                    <option value="">Elegir…</option>
                    {promos.map((pr) => (
                      <option key={pr.id} value={pr.id} disabled={!pr.active}>
                        {pr.name}{pr.active ? "" : " (inactiva)"}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              {(prize.kind === "percent" || prize.kind === "fixed") && (
                <div className="sm:col-span-2 space-y-1">
                  <Label className="text-xs">Valor</Label>
                  <Input
                    type="number"
                    min={0}
                    value={prize.value ?? 0}
                    onChange={(e) => updatePrize(prize.id, { value: Number(e.target.value) || 0 })}
                  />
                </div>
              )}
              <div className="sm:col-span-2 space-y-1">
                <Label className="text-xs">Probabilidad (peso)</Label>
                <Input
                  type="number"
                  min={0}
                  value={prize.weight}
                  onChange={(e) => updatePrize(prize.id, { weight: Number(e.target.value) || 0 })}
                />
              </div>
              <div className="sm:col-span-1 flex items-end justify-end">
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="text-destructive"
                  disabled={prizes.length <= 1}
                  onClick={() => setPrizes((prev) => prev.filter((p) => p.id !== prize.id))}
                  title="Quitar premio"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>

        <div className="flex flex-wrap gap-2">
          <Button onClick={() => void save()} disabled={saving}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Guardar"}
          </Button>
          <Button type="button" variant="outline" onClick={() => void load()} disabled={saving}>
            Descartar cambios
          </Button>
        </div>
      </ClinicalPageShell>
    </div>
  );
}
